<script lang="ts">
import debounce from 'lodash/debounce'
import type { Ref } from 'vue'
import {
  computed,
  defineComponent,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  toRaw,
  watch,
} from 'vue'
import { useIntervalFn, useScriptTag } from '@vueuse/core'
import { useEnhancer } from '@/app/enhancer'
import { useCustomization } from '@/hooks/useCustomization'
import { INITIAL_SCRIPTS } from '@/configs/app.config'
import { lenOptions } from '@/hooks/enums'
import { EventType, useEvents } from '@/hooks/useEvents'
import { useZoom } from '@/hooks/useGesture'
import { hexToRgbA } from '@/utils/color'
import { dataURLToBlob } from '@/utils/image'
import { differenceTwoObject } from '@/utils/object'

export default defineComponent({
  name: 'TryOn',
  props: {
    isTryOn: {
      type: Boolean,
      required: true,
      default: () => false,
    },
    isBack: {
      type: Boolean,
      default: () => false,
    },
    isGuide: {
      type: Boolean,
      default: () => false,
    },
  },
  setup(props, { expose }) {
    const eventBus = useEvents()
    const customization = useCustomization()
    const { storeFavorites, storeGlobalLoading } = useEnhancer()
    const isScriptsLoaded = ref(false)
    const container: Ref<HTMLElement | any> = ref()
    const glassesCanvasEl: Ref<HTMLCanvasElement | any> = ref()
    let glassesView: GlassesView | null = null
    let autoTimer: any

    const captureCanvasEl: Ref<HTMLCanvasElement | any> = ref()
    let captureView: GlassesView | null = null

    const { pressed } = useZoom(container, glassesCanvasEl)
    const isDemoMode = computed(() => customization.getCurrentDemoMode.value)
    const isSyncedActive = computed(() => storeFavorites.isSynced)

    const isShowMirror = computed(() => {
      return storeFavorites.selectedLenOption.includes(lenOptions.MIRROR)
    })

    const mappingLogoToggle = (key: string | undefined) => {
      const defineMap = {
        black: 'on',
        white: 'on',
        clear: 'on',
      }
      return defineMap[key || ''] ?? 'off'
    }

    const mappingLogoColor = (key: string | undefined) => {
      const defineMap = {
        clear: new THREE.Vector4(0, 0, 0, 0),
        black: new THREE.Vector4(0, 0, 0, 1),
        white: new THREE.Vector4(1, 1, 1, 1),
      }
      return defineMap[key || ''] ?? defineMap.clear
    }

    const composeReflection = (len: LensColorType | AnyObject) => {
      // * [No.49] Try-on mode reflection is 0.8, 360 viewer reflection is 0.6
      if (isShowMirror.value)
        return +len.uiMirror
      // if (isShowMirror.value && !props.isTryOn) return 0.5;
      return +len.reflection
    }

    const applyConfig = async (
      glassesView: GlassesView | null,
      config: ConfigurationType,
    ) => {
      try {
        const {
          model,
          frame,
          temple,
          len,
          logoFrontColor,
          logoBackColor,
          bridge,
        } = config

        // Model
        if (model) {
          storeGlobalLoading.start()

          try {
            await glassesView?.tryGlasses(model.modelUrl, '3D')
          }
          catch {
            const corsBypassUrl = `${model.modelUrl}?${Math.random()}`
            await glassesView?.tryGlasses(corsBypassUrl, '3D')
          }
          // Zoom out a little bit to make the model look better in landscape mode
          if (container.value.clientWidth > container.value.clientHeight)
            new Array(1).fill(0).forEach(() => glassesView?.zoomOut?.())

          // Hinge
          glassesView?.currentGlasses?.setFilter('hinge', {
            color: hexToRgbA('#666666', 1),
          })

          if (model?.extraParts) {
            model.extraParts.forEach((e) => {
              glassesView?.currentGlasses?.switchPart(e.key, e.value)
            })
          }
        }

        // Frame
        if (frame) {
          let leftTexture: any
          let rightTexture: any

          if (frame.photo) {
            const [leftPhoto, rightPhoto] = frame.photo.split(',')
            try {
              leftTexture = await Utility.loadTextureAsync(leftPhoto)
              if (rightPhoto)
                rightTexture = await Utility.loadTextureAsync(rightPhoto)
            }
            catch {
              const corsBypassUrl = `${leftPhoto}?${Math.random()}`
              leftTexture = await Utility.loadTextureAsync(corsBypassUrl)

              if (rightPhoto) {
                const corsBypassRightUrl = `${rightPhoto}?${Math.random()}`
                rightTexture = await Utility.loadTextureAsync(
                  corsBypassRightUrl,
                )
              }
            }
          }

          // * Cheat support core lib render order
          const modifyTransparent
            = +frame.transparent === 1 ? 0.999 : +frame.transparent

          const frameColorRgba = {
            ...(frame.color && {
              color: hexToRgbA(frame.color, modifyTransparent),
            }),
            ...(leftTexture && {
              color: new THREE.Vector4(1, 1, 1, +frame.transparent || 1),
              texture: leftTexture,
            }),
          }
          const filter = {
            ...frameColorRgba,
            ...(frame.reflection && { reflection: +frame.reflection }),
            ...(frame.brightness && { brightness: +frame.brightness }),
            ...(frame.contrast && { contrast: +frame.contrast }),
            ...(frame.saturation && { saturation: +frame.saturation }),
          }

          const filterRight = {
            ...filter,
            ...(rightTexture && {
              color: new THREE.Vector4(1, 1, 1, +frame.transparent || 1),
              texture: rightTexture,
            }),
          }

          // ! exception case... don't edit
          // * for left frame
          glassesView?.currentGlasses?.setFilter('lenses_pad_front', filter)
          glassesView?.currentGlasses?.setFilter('lenses_pad_back', filter)
          // * for right frame
          glassesView?.currentGlasses?.setFilter(
            'right_lenses_pad_front',
            filterRight,
          )
          glassesView?.currentGlasses?.setFilter(
            'right_lenses_pad_back',
            filterRight,
          )
          glassesView?.currentGlasses?.setFilter('lenses_pad', {
            ...frameColorRgba, // hexToRgbA('#ffffff', '0.8'),
          })
        }

        // Temple
        if (temple) {
          const templeParams = {
            color: hexToRgbA(temple.color, +temple.transparent),
            ...(temple.reflection && { reflection: +temple.reflection }),
            ...(temple.brightness && { brightness: +temple.brightness }),
            ...(temple.contrast && { contrast: +temple.contrast }),
            ...(temple.saturation && { saturation: +temple.saturation }),
          }
          glassesView?.currentGlasses?.setFilter('frame', templeParams)
        }

        // Lens
        if (len) {
          const isLenClear = len.value === 'clear'
          const modifyTransparent = isLenClear
            ? !props.isTryOn
                ? 0.4
                : +len.transparent
            : +len.transparent

          let texture: any
          if (len.photo) {
            try {
              texture = await Utility.loadTextureAsync(len.photo)
            }
            catch {
              const corsBypassUrl = `${len.photo}?${Math.random()}`
              texture = await Utility.loadTextureAsync(corsBypassUrl)
            }
          }
          glassesView?.currentGlasses?.setLensesFilter({
            ...(len.color && {
              color: hexToRgbA(len.color, modifyTransparent),
            }),
            ...(texture && {
              color: new THREE.Vector4(1, 1, 1, modifyTransparent),
              texture,
              mode: 'GRADIENT',
            }),
            ...(len.reflection && { reflection: +len.reflection }),
            ...(len.brightness && { brightness: +len.brightness }),
            ...(len.contrast && { contrast: +len.contrast }),
            ...(len.saturation && { saturation: +len.saturation }),
            ...(isShowMirror.value && {
              reflection: composeReflection(len),
            }),
            // glassesView?.currentGlasses?.getFilter('lenses').sceneLens
            sceneLens: true, // props.isTryOn || isShowMirror.value || !props.isTryOn,
          })
        }

        // ! Logo front (exception case ... don't edit)
        if (logoFrontColor !== undefined) {
          const frontLogoState = mappingLogoToggle(logoFrontColor)
          const color = mappingLogoColor(logoFrontColor)
          const colorMix = {
            color,
            reflection: '1',
            brightness: '1',
            contrast: '1',
            saturation: '1',
          }
          glassesView?.currentGlasses?.switchPart('front_logo', frontLogoState)
          glassesView?.currentGlasses?.setFilter('logo', colorMix)
        }

        // ! Logo back (exception case ... don't edit)
        if (logoBackColor !== undefined) {
          const backLogoState = mappingLogoToggle(logoBackColor)
          const color = mappingLogoColor(logoBackColor)
          const colorMix = {
            color,
            reflection: '1',
            brightness: '1',
            contrast: '1',
            saturation: '1',
          }
          glassesView?.currentGlasses?.switchPart('back_logo', backLogoState)
          glassesView?.currentGlasses?.setFilter('logo_back', colorMix)
        }

        if (bridge) {
          const value = bridge.extraParts[0].value
          glassesView?.currentGlasses?.switchPart('bridge', value)
        }

        glassesView?.refresh()
      }
      catch (err) {
        console.warn(err)
      }
      storeGlobalLoading.end()
    }

    const magnifyFaceCtx = useIntervalFn(() => {
      if (!props.isTryOn)
        return

      if (!glassesView?.getFaceInfo()) {
        container.value.style.transform = 'scale(1)'
        return
      }

      const previewSize = 480
      // console.log(glassesView?.getFaceInfo())
      const faceWidth = glassesView?.getFaceInfo().faceSize
      // Assume that face height is double face width
      const faceHeight = faceWidth * 2

      const viewPort = { width: container.value.clientWidth, height: container.value.clientHeight }
      const currentScale = container.value.style.transform?.match(/scale\((\d+(?:\.\d+)?)\)/)?.[1] ?? 1
      const targetScale = viewPort.width > viewPort.height
        ? Math.max(1, (previewSize * 0.6) / faceHeight)
        : Math.max(1, (previewSize * 0.3) / faceWidth)

      const facePos = glassesView?.getFaceInfo().facePosition
      const currentTransX = container.value.style.transform?.match(/translate\((\d+(?:\.\d+)?)px, (\d+(?:\.\d+)?)px\)/)?.[1] ?? 0
      const currentTransY = container.value.style.transform?.match(/translate\((\d+(?:\.\d+)?)px, (\d+(?:\.\d+)?)px\)/)?.[2] ?? 0
      const allowedDeltaX = viewPort.width * (targetScale - 1) / 4
      const allowedDeltaY = viewPort.height * (targetScale - 1) / 4
      let transX = viewPort.width / 2 - facePos.x
      let transY = viewPort.height / 2 - facePos.y
      if (Math.abs(transX) > allowedDeltaX)
        transX = Math.sign(transX) * allowedDeltaX
      if (Math.abs(transY) > allowedDeltaY)
        transY = Math.sign(transY) * allowedDeltaY

      // console.log(targetScale, currentScale)
      if (Math.abs(targetScale - +currentScale) > 0.1 || Math.abs(transX - +currentTransX) > 100 || Math.abs(transY - +currentTransY) > 100) {
        container.value.style.transform = `scale(${targetScale}) translate(${transX}px, ${transY}px)`
        container.value.style.transition = 'all 1s'
      }
    }, 1000, { immediate: false })

    const renderGlassesView = async (isTryOn: boolean) => {
      glassesCanvasEl.value.width = container.value.clientWidth
      glassesCanvasEl.value.height = container.value.clientHeight

      glassesView = new (isTryOn ? TryOnView : GlassesView)(
        'jeeFaceFilterCanvas',
        container.value.getRootNode(),
      )
      await glassesView.start()
      glassesView.turnOnFaceSelection?.()

      await applyConfig(glassesView, storeFavorites.currentConfig)
      if (!isTryOn)
        nextTick(() => glassesView?.rotateCenter(0, 0, 0))

      if (isDemoMode.value && isSyncedActive.value)
        magnifyFaceCtx.resume()
    }

    const renderCaptureView = async () => {
      // Create glasses view that is dedicated to make thumbnail
      captureCanvasEl.value.width = container.value.clientWidth
      captureCanvasEl.value.height = container.value.clientWidth * 0.75

      captureView = new GlassesView(
        'captureCanvas',
        container.value.getRootNode(),
      )
      await captureView.start()

      captureView?.autoZoom(captureView.rootObject, '3D')
      new Array(2).fill(0).forEach(() => captureView?.zoomIn())
    }

    const applyChange = debounce(
      ({ /* name, store, after, onError */ args }) => {
        // * Wait utils lib loaded
        if (!glassesView)
          return

        const [key, newValue] = args as [string, AnyObject]

        // * Apply len/lenOption part configuration
        if (key === 'lensOptions')
          return applyConfig(glassesView, { len: storeFavorites.selectedLen })

        // * Consider initial configuration
        if (typeof key !== 'string') {
          const [initialConfig] = args
          return applyConfig(glassesView, initialConfig)
        }

        if (key === 'model')
          return applyConfig(glassesView, storeFavorites.currentConfig)

        // * Apply specific part configuration
        return applyConfig(glassesView, { [key]: newValue })
      },
      50,
    )

    const getMainDataUrl = async () => {
      const img = await glassesView?.snapshot()
      return img?.src ? img.src : ''
    }

    const triggerAutoRotate = () => {
      if (props.isTryOn)
        return

      // Auto rotate in 10 seconds for one lap
      const fps = 60
      const timePerFrame = (1 * 1000) / fps
      const totalFramePerLap = (10 * 1000) / timePerFrame
      if (autoTimer) {
        clearInterval(autoTimer)
        autoTimer = null
      }
      else {
        let frameCount = 0
        autoTimer = setInterval(() => {
          glassesView?.rotateCenter(0, (2 * Math.PI) / totalFramePerLap, 0)

          frameCount++
          if (frameCount === totalFramePerLap) {
            clearInterval(autoTimer)
            autoTimer = null
            eventBus.emit(EventType.ROTATE_END)
          }
        }, timePerFrame)
      }
    }

    const triggerZoomIn = (zoomLevel) => {
      if (props.isTryOn) {
        magnifyFaceCtx.pause()
        container.value.style.transform = `scale(1.${zoomLevel * 2 - 1})`
      }
      else {
        glassesView?.zoomIn()
      }
    }

    const triggerZoomOut = (zoomLevel) => {
      if (props.isTryOn) {
        magnifyFaceCtx.pause()
        container.value.style.transform = `scale(1.${zoomLevel * 2 - 1})`
      }
      else {
        glassesView?.zoomOut()
      }
    }

    const triggerSwitchCamera = () => {
      glassesView?.switchVideoSource()
    }

    const triggerOpenGlass = (isOpenGlass: boolean) => {
      if (isOpenGlass)
        glassesView?.currentGlasses?.closeGlasses()
      else glassesView?.currentGlasses?.openGlasses()
    }

    // TODO: Release resource for low end devices
    const clearGlassesView = (isInit = false) => {
      if (!props.isTryOn)
        return
      // * Init new
      if (isInit)
        return renderGlassesView(props.isTryOn)

      // ! temporary clear resource
      return glassesView?.stop?.()
    }

    const loadGlassOn = async (isTryOn: boolean) => {
      if (!glassesCanvasEl.value || !container.value)
        return

      await Promise.allSettled([renderGlassesView(isTryOn), renderCaptureView()])
    }

    const { load, unload } = useScriptTag(
      INITIAL_SCRIPTS[0],
      () => {
        isScriptsLoaded.value = true
        nextTick(() => loadGlassOn(props.isTryOn))
      },
      {
        async: true,
        defer: true,
        manual: true,
      },
    )

    onMounted(() => {
      eventBus.on(EventType.ROTATE_AUTO, triggerAutoRotate)
      eventBus.on(EventType.ZOOM_IN, triggerZoomIn)
      eventBus.on(EventType.ZOOM_OUT, triggerZoomOut)
      eventBus.on(EventType.TOGGLE_OPEN_GLASS, triggerOpenGlass)
      eventBus.on(EventType.SWITCH_CAMERA, triggerSwitchCamera)

      eventBus.on(EventType.ADD_TO_MY_LIST, clearGlassesView)
      eventBus.on(EventType.SHARE_BY_SNS, clearGlassesView)
      eventBus.on(EventType.SHARE_WITH_LINE, clearGlassesView)
    })

    // * Main configuration apply function
    const unsubscribe = storeFavorites.$onAction(applyChange)
    // !: Clear all subscriptions
    onBeforeUnmount(() => {
      unload()
      unsubscribe()
      eventBus.off(EventType.ROTATE_AUTO)
      eventBus.off(EventType.ZOOM_IN)
      eventBus.off(EventType.ZOOM_OUT)
      eventBus.off(EventType.TOGGLE_OPEN_GLASS)
      eventBus.off(EventType.ADD_TO_MY_LIST)
      eventBus.off(EventType.SHARE_BY_SNS)
      eventBus.off(EventType.SHARE_WITH_LINE)
      eventBus.off(EventType.SWITCH_CAMERA)
      glassesView?.stop?.()
    })

    const getFormDataThumbnail = async () => {
      try {
        await applyConfig(captureView, storeFavorites.currentConfig)
        captureView?.rotateCenter(0, 0, 0)
        let img = await captureView?.snapshot()
        if (!img?.src)
          img = await glassesView?.snapshot()

        const blob = await dataURLToBlob(img?.src ?? '')
        const form = new FormData()
        form.append('thumbnail', blob, 'thumbnail.png')
        // ? Turn try on when build thumbnail is completed
        clearGlassesView(true)

        return form
      }
      catch (error) {
        console.error({ error })
      }
    }

    const handleStoreFavoritesChange = (newValue, oldValue) => {
      if (!isDemoMode.value)
        return
      const diffData = differenceTwoObject(toRaw(newValue), toRaw(oldValue))
      const modelArgsLength = Object.keys(diffData).length

      if (modelArgsLength > 4 && !diffData.model)
        return

      if (modelArgsLength >= 3 && diffData.model)
        return applyChange({ args: ['model', diffData] })

      if (modelArgsLength >= 3 && !diffData.model)
        return applyChange({ args: [diffData] })

      return applyChange({ args: Object.entries(diffData).flat() })
    }

    expose({
      getFormDataThumbnail,
      getMainDataUrl,
    })

    watch(() => props.isGuide, debounce((isAccepted) => {
      // Load scripted if need
      !isAccepted && load()
    }, 500), { immediate: true })

    watch(
      () => props.isTryOn,
      () => {
        // Release memory before constructing new
        glassesView?.stop?.()
        captureView?.stop?.()
      },
      { flush: 'pre', immediate: true },
    )

    watch(
      () => props.isTryOn,
      (isTryOn) => {
        loadGlassOn(isTryOn)
      },
      { flush: 'post', immediate: false },
    )

    watch(
      () => isSyncedActive.value,
      (newValue) => {
        if (newValue) {
          magnifyFaceCtx.resume()
        }
        else {
          magnifyFaceCtx.pause()
          delete container.value.style.transform
        }
      },
    )

    // TODO: Watch data change to apply change
    watch(() => storeFavorites.currentConfig, (newValue, oldValue) => handleStoreFavoritesChange(newValue, oldValue), { flush: 'post', immediate: false })

    return {
      isScriptsLoaded,
      container,
      glassesCanvasEl,
      captureCanvasEl,
      pressed,
      // pinchHandler,
      // interacting,
      // hoverHandler,
    }
  },
})
</script>

<template>
  <section v-if="isScriptsLoaded" class="go-main">
    <div v-if="isTryOn" ref="container" class="fit">
      <canvas
        id="captureCanvas"
        ref="captureCanvasEl"
        style="visibility: hidden"
      />
      <canvas id="jeeFaceFilterCanvas" ref="glassesCanvasEl" />
      <video id="video" playsinline style="display: none" />
    </div>
    <div
      v-else
      ref="container"
      class="fit"
      :class="[pressed ? 'cursor-grabbing' : 'cursor-grab']"
    >
      <canvas
        id="captureCanvas"
        ref="captureCanvasEl"
        style="visibility: hidden"
      />
      <canvas
        id="jeeFaceFilterCanvas"
        ref="glassesCanvasEl"
        class="z-vr r360 absolute-center"
        :class="[{ 'z-default': isBack }]"
      />
    </div>
  </section>
</template>
